"use client";

import { Link } from "@/i18n/navigation";
import styles from "./mobile-drawer.module.css";
import {
  Close,
  Language,
  KeyboardArrowRight,
  Person,
} from "@mui/icons-material";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useDomainContext } from "@/global-context/domain-context";
import {
  getLocaleDataFromDomain,
  getLocaleTarget,
} from "@/lib/locale-data/locale";
import Image from "next/image";
import { CircleFlag } from "react-circle-flags";

export default function MobileDrawer({
  isOpen,
  onClose,
  isAuthenticated,
  onOpenSettings,
  onSignOut,
  onSignIn,
  locale,
}: {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  isAuthenticated: boolean;
  onSignOut: () => void;
  onSignIn: () => void;
  locale: string;
}) {
  const { userData } = useUserAuthDataContext();
  const domain = useDomainContext();
  const localeData = getLocaleTarget({ domain, locale });
  const unitFormat = userData.unit_system === "Metric" ? "m²" : "sqft";
  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.drawerHeader}>
          {isAuthenticated ? (
            <div className={styles.authHeader}>
              <Person /> <span>My Account</span>
            </div>
          ) : (
            <span className={styles.menuTitle}>Menu</span>
          )}
          <button onClick={onClose} className={styles.closeBtn}>
            <Close />
          </button>
        </div>

        {/* Navigation Links - ALL OF THEM */}
        <div className={styles.linksSection}>
          <Link href="/buy" className={styles.drawerLink} onClick={onClose}>
            Buy
          </Link>
          <Link href="/rent" className={styles.drawerLink} onClick={onClose}>
            Rent
          </Link>
          <Link
            href="/new-developments"
            className={styles.drawerLink}
            onClick={onClose}
          >
            New Developments
          </Link>
          <Link href="/invest" className={styles.drawerLink} onClick={onClose}>
            Invest
          </Link>
          <Link
            href="/commercial"
            className={styles.drawerLink}
            onClick={onClose}
          >
            Commercial
          </Link>
          <Link href="/agents" className={styles.drawerLink} onClick={onClose}>
            Find Agents
          </Link>
          <Link
            href="/resources"
            className={styles.drawerLink}
            onClick={onClose}
          >
            Resources
          </Link>
        </div>

        <hr className={styles.divider} />

        {/* Global Settings Trigger (Language / Currency / Units) */}
        <div className={styles.prefsSection}>
          <button
            className={styles.prefItem}
            onClick={() => {
              onClose();
              onOpenSettings(); // Trigger setIsSettingsOpen(true) from parent
            }}
          >
            <div className={styles.prefLabel}>
              {localeData.region === "GLOBAL" ? (
                <Language className={styles.iconSm} />
              ) : (
                <CircleFlag
                  countryCode={localeData.region.toLowerCase()}
                  className={styles.prefIcon}
                />
              )}
              <span>Global Settings</span>
            </div>
            <div className={styles.prefValue}>
              <span>
                {localeData.region === "GLOBAL" ? "EN" : localeData.region} |{" "}
                {userData.currency} | {unitFormat}
              </span>
              <KeyboardArrowRight />
            </div>
          </button>
        </div>

        {/* Footer Action */}
        <div className={styles.drawerFooter}>
          {!isAuthenticated ? (
            <button
              className={styles.signInBtnMobile}
              onClick={() => {
                onClose();
                onSignIn();
              }}
            >
              Sign In / Register
            </button>
          ) : (
            <button
              className={styles.signOutBtnMobile}
              onClick={() => {
                onClose();
                onSignOut(); // TRIGGER SIGN OUT
              }}
            >
              Sign Out
            </button>
          )}
          <Link href="/new-listing" className={styles.cta} onClick={onClose}>
            List Property
          </Link>
        </div>
      </div>
    </div>
  );
}
